'use client';

/**
 * AddFundsWizard - Add Funds Flow (Sheet)
 * 
 * Two-step wizard for adding funds to SupaWallet:
 * 1. Choose method (Crypto or Bank Transfer)
 * 2. Show/Create address based on selection
 * 
 * Uses same Sheet pattern as P2PTransferSheet:
 * - Mobile (<1024px): Bottom sheet, h=95vh
 * - Desktop (≥1024px): Right sheet, w=28rem
 * 
 * @see PRD-WALLET-HUB
 */

import { NetworkIcon } from '@/components/NetworkIcons';
import { AppleQR } from '@/components/ui/AppleQR';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CurrencyIcon } from '@/components/ui/CurrencyIcon';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { Heading, Text } from '@/components/ui/typography';
import { DepositAddress, useDepositAddress, useVirtualAccount } from '@/features/user/wallet/hooks/useWallet';
import { useIsMobile } from '@/hooks/use-mobile';
import { useGeoInBackground } from '@/hooks/useGeoInBackground';
import { useVirtualAccountFullDetails, useVirtualAccounts, type VirtualAccountInstruction } from '@/hooks/useVirtualAccounts';
import { cn } from '@/lib/utils';
import { semanticTokens } from '@packages/config/theme';
import { SFIcon } from '@/components/ui/sf-icon';
import { sfIcons } from '@/lib/sf-icons';
import { InlineLoadingIndicator } from '@/components/ui/inline-loading';
import { Share2, Zap } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from '@/hooks/use-toast';

// Supported chains for crypto deposits
const SUPPORTED_CHAINS = [
  { id: 'base', name: 'Base', currency: 'USDC', fee: 'Low', recommended: true },
  { id: 'ethereum', name: 'Ethereum', currency: 'USDC', fee: 'Variable' },
  { id: 'solana', name: 'Solana', currency: 'USDC', fee: 'Very Low' },
  { id: 'polygon', name: 'Polygon', currency: 'USDC', fee: 'Low' },
  { id: 'arbitrum', name: 'Arbitrum', currency: 'USDC', fee: 'Low' },
  { id: 'optimism', name: 'Optimism', currency: 'USDC', fee: 'Low' },
];

// Supported fiat currencies
const ALL_SUPPORTED_FIAT = [
  { id: 'USD', name: 'US Dollar', type: 'Wire / ACH', geoRestriction: null },
  { id: 'EUR', name: 'Euro', type: 'SEPA', geoRestriction: null },
  { id: 'MXN', name: 'Mexican Peso', type: 'SPEI', geoRestriction: 'MX' },
  { id: 'BRL', name: 'Brazilian Real', type: 'PIX (Beta)', geoRestriction: 'BR' },
];

type WizardStep = 'choose' | 'crypto' | 'fiat';
type FiatSubStep = 'select-currency' | 'show-account';

interface AddFundsWizardProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export function AddFundsWizard({ open, onClose, onSuccess }: AddFundsWizardProps) {
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  // Wizard state
  const [step, setStep] = useState<WizardStep>('choose');
  const [fiatSubStep, setFiatSubStep] = useState<FiatSubStep>('select-currency');
  const [selectedChain, setSelectedChain] = useState<string | null>(null);
  const [selectedFiat, setSelectedFiat] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Generated data
  const [generatedAddress, setGeneratedAddress] = useState<DepositAddress | null>(null);
  const [selectedVA, setSelectedVA] = useState<VirtualAccountInstruction | null>(null);

  // Geo detection
  const geo = useGeoInBackground();

  // Mutations
  const depositAddressMutation = useDepositAddress();
  const virtualAccountMutation = useVirtualAccount();

  // Virtual accounts data
  const { data: virtualAccountsData, isLoading: isLoadingVA } = useVirtualAccounts(open);

  // Full details for selected VA (unmasked)
  const { data: fullDetails, isLoading: isLoadingFullDetails } = useVirtualAccountFullDetails(selectedVA?.id ?? null);

  // Filter fiat currencies based on geo
  const SUPPORTED_FIAT = useMemo(() => {
    const userCountry = geo.country?.toUpperCase();
    return ALL_SUPPORTED_FIAT.filter(fiat => {
      if (!fiat.geoRestriction) return true;
      if (fiat.id === 'MXN' && userCountry === 'MX') return true;
      if (fiat.id === 'BRL' && userCountry === 'BR') return true;
      return false;
    });
  }, [geo.country]);

  // Filter VAs by selected currency
  const filteredVAs = useMemo(() => {
    if (!virtualAccountsData?.accounts || !selectedFiat) return [];
    const selectedCurrency = selectedFiat.toUpperCase();
    return virtualAccountsData.accounts.filter(va => {
      const vaCurrency = (va.currency || '').toUpperCase();
      const vaSourceCurrency = ((va as any).source_currency || '').toUpperCase();
      const currencyMatch = vaCurrency === selectedCurrency || vaSourceCurrency === selectedCurrency;
      const status = va.status?.toLowerCase() || '';
      const statusMatch = !status || status === 'activated' || status === 'active';
      return currencyMatch && statusMatch;
    });
  }, [virtualAccountsData, selectedFiat]);

  // Reset state when sheet closes
  useEffect(() => {
    if (!open) {
      setStep('choose');
      setFiatSubStep('select-currency');
      setSelectedChain(null);
      setSelectedFiat(null);
      setGeneratedAddress(null);
      setSelectedVA(null);
      setCopied(false);
    }
  }, [open]);

  // Handle copy single field
  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Copied to clipboard');
  };

  // Generate formatted bank details text for copy/share
  const generateBankDetailsText = useCallback(() => {
    const details = fullDetails || selectedVA;
    if (!details) return '';

    const lines: string[] = [
      `Bank Details for ${selectedFiat?.toUpperCase()} Transfer`,
      '',
    ];

    // Beneficiary Details
    if (details.beneficiaryName) {
      lines.push('── Beneficiary Details ──');
      lines.push(`Beneficiary Name: ${details.beneficiaryName}`);
      if (details.beneficiaryAddress) {
        lines.push(`Beneficiary Address: ${details.beneficiaryAddress}`);
      }
      lines.push('');
    }

    // Bank Details
    lines.push('── Bank Details ──');
    if (details.bankName) lines.push(`Bank Name: ${details.bankName}`);
    if (details.bankAddress) lines.push(`Bank Address: ${details.bankAddress}`);
    if (details.iban) lines.push(`IBAN: ${details.iban}`);
    if (details.bic) lines.push(`BIC/SWIFT: ${details.bic}`);
    if (details.accountNumber) lines.push(`Account Number: ${details.accountNumber}`);
    if (details.routingNumber) lines.push(`ACH Routing: ${details.routingNumber}`);
    if (details.wireRoutingNumber && details.wireRoutingNumber !== details.routingNumber) {
      lines.push(`Wire Routing: ${details.wireRoutingNumber}`);
    }
    if (details.accountType) lines.push(`Account Type: ${details.accountType}`);
    if (details.clabe) lines.push(`CLABE: ${details.clabe}`);
    if (details.pixKey) lines.push(`PIX Key: ${details.pixKey}`);
    lines.push('');

    // Reference (Important!)
    if (details.reference || details.memo) {
      lines.push('── Reference (IMPORTANT) ──');
      if (details.reference) lines.push(`Reference: ${details.reference}`);
      if (details.memo) lines.push(`Memo: ${details.memo}`);
      lines.push('');
    }

    lines.push('Include the reference number in your bank transfer.');
    lines.push('');
    lines.push('Sent from Supabank');

    return lines.join('\n');
  }, [fullDetails, selectedVA, selectedFiat]);

  // Handle copy all details
  const handleCopyAll = useCallback(() => {
    const text = generateBankDetailsText();
    if (!text) return;

    navigator.clipboard.writeText(text);
    toast.success('All bank details copied');
  }, [generateBankDetailsText]);

  // Handle share (native share API on mobile, fallback to copy)
  const handleShare = useCallback(async () => {
    const text = generateBankDetailsText();
    if (!text) return;

    // Check if native share is available (mobile)
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${selectedFiat?.toUpperCase()} Bank Details`,
          text: text,
        });
        toast.success('Shared successfully');
      } catch (err) {
        // User cancelled or error
        if ((err as Error).name !== 'AbortError') {
          // Fallback to copy
          navigator.clipboard.writeText(text);
          toast.success('Copied to clipboard');
        }
      }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(text);
      toast.success('Copied to clipboard');
    }
  }, [generateBankDetailsText, selectedFiat]);

  // Navigate to virtual accounts list
  const handleViewAccounts = () => {
    onClose();
    navigate('/dashboard/virtual-accounts');
  };

  // Handle chain selection (crypto)
  const handleSelectChain = async (chainId: string) => {
    setSelectedChain(chainId);
    setGeneratedAddress(null);
    try {
      const result = await depositAddressMutation.mutateAsync({ chain: chainId, currency: 'USDC' });
      setGeneratedAddress(result);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to generate address');
    }
  };

  // Handle fiat currency selection
  const handleSelectFiatCurrency = (currencyId: string) => {
    setSelectedFiat(currencyId);
    setFiatSubStep('show-account');
  };

  // Handle VA selection or creation
  const handleSelectVA = (va: VirtualAccountInstruction) => {
    setSelectedVA(va);
  };

  // Handle create new VA
  const handleCreateVA = async () => {
    if (!selectedFiat) return;
    try {
      const result = await virtualAccountMutation.mutateAsync({
        currency: selectedFiat.toLowerCase() as 'usd' | 'eur' | 'mxn' | 'brl'
      });
      // Convert to instruction format with ALL bank details
      const di = result.deposit_instructions || {};
      const newVA: VirtualAccountInstruction = {
        id: result.va_id,
        currency: selectedFiat.toUpperCase(),
        status: 'active',
        paymentRail: null,
        paymentRails: di.rails || [],
        // Bank details
        bankName: di.bank_name || null,
        bankAddress: di.bank_address || null,
        // Beneficiary (CRITICAL for transfers!)
        beneficiaryName: di.beneficiary_name || null,
        beneficiaryAddress: di.beneficiary_address || null,
        // SEPA (EUR)
        iban: di.iban || null,
        bic: di.bic || di.swift || null,
        // ACH (USD)
        accountNumber: di.account || null,
        routingNumber: di.routing || null,
        wireRoutingNumber: di.wire_routing || null,
        accountType: di.account_type || null,
        // SPEI (MXN)
        clabe: di.clabe || null,
        // PIX (BRL)
        pixKey: di.pix_key || null,
        pixKeyType: di.pix_key_type || null,
        // Reference
        reference: di.reference || null,
        memo: di.memo || null,
        // Other
        destinationCurrency: null,
        destinationAddress: null,
        createdAt: null,
        updatedAt: null,
      };
      setSelectedVA(newVA);
      onSuccess?.();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to create account');
    }
  };

  // Go back handler
  const handleBack = () => {
    if (step === 'crypto') {
      if (selectedChain) {
        setSelectedChain(null);
        setGeneratedAddress(null);
      } else {
        setStep('choose');
      }
    } else if (step === 'fiat') {
      if (fiatSubStep === 'show-account') {
        if (selectedVA) {
          setSelectedVA(null);
        } else {
          setFiatSubStep('select-currency');
          setSelectedFiat(null);
        }
      } else {
        setStep('choose');
      }
    }
  };

  const sheetSide = isMobile ? 'bottom' : 'right';
  const showBackButton = step !== 'choose';

  // Get title based on step
  const getTitle = () => {
    if (step === 'choose') return 'Add Funds';
    if (step === 'crypto') return selectedChain ? 'Deposit Address' : 'Select Network';
    if (step === 'fiat') {
      if (fiatSubStep === 'select-currency') return 'Select Currency';
      return selectedVA ? 'Bank Details' : 'Select Account';
    }
    return 'Add Funds';
  };

  // Get description based on step
  const getDescription = () => {
    if (step === 'choose') return 'Choose how you want to add funds';
    if (step === 'crypto') return selectedChain ? 'Send USDC to this address' : 'Choose blockchain network';
    if (step === 'fiat') {
      if (fiatSubStep === 'select-currency') return 'Which currency will you send?';
      return selectedVA ? 'Use these details to send a bank transfer' : 'Select or create a deposit account';
    }
    return '';
  };

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent
        side={sheetSide}
        className={cn(
          'flex flex-col',
          isMobile
            ? 'h-[95vh] max-h-[95vh]'
            : 'w-[28rem] max-w-[28rem]',
          semanticTokens.motion.standard
        )}
      >
        {/* Header */}
        <SheetHeader className="flex-shrink-0 pb-4 border-b">
          <div className="flex items-center gap-3">
            {showBackButton && (
              <Button
                variant="ghost"
                size="icon"
                onClick={handleBack}
                className="min-h-[44px] min-w-[44px] -ml-2"
                aria-label="Go back"
              >
                <SFIcon icon={sfIcons.back} className="h-5 w-5" />
              </Button>
            )}
            <div className={cn(
              'p-2.5 rounded-xl',
              semanticTokens.bg.muted
            )}>
              <SFIcon icon={sfIcons.add} className="h-5 w-5 text-primary" aria-hidden="true" />
            </div>
            <div>
              <SheetTitle className={semanticTokens.typography.h3}>
                {getTitle()}
              </SheetTitle>
              <SheetDescription className={cn(semanticTokens.typography.caption, 'mt-0.5')}>
                {getDescription()}
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden py-6 space-y-4 min-w-0">

          {/* Step: Choose Method */}
          {step === 'choose' && (
            <div className="grid gap-4">
              {/* Crypto Option */}
              <Card
                className={cn(
                  'cursor-pointer transition-all',
                  semanticTokens.motion.standard,
                  'hover:shadow-md hover:border-primary/50',
                  'focus-visible:ring-2 focus-visible:ring-primary'
                )}
                onClick={() => setStep('crypto')}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && setStep('crypto')}
              >
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className={cn(
                      'p-3 rounded-xl',
                      'bg-orange-100 dark:bg-orange-900/30'
                    )}>
                      <SFIcon icon={sfIcons.wallet} className="h-6 w-6 text-orange-600 dark:text-orange-400" />
                    </div>
                    <div className="flex-1">
                      <Heading level={4} className="mb-1">Crypto</Heading>
                      <Text variant="sm" color="muted">
                        Deposit USDC from any wallet
                      </Text>
                      <div className="flex items-center gap-2 mt-3">
                        <Zap className="h-4 w-4 text-green-500" />
                        <Text variant="caption" color="muted">
                          Instant • Low fees
                        </Text>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Bank Transfer Option */}
              <Card
                className={cn(
                  'cursor-pointer transition-all',
                  semanticTokens.motion.standard,
                  'hover:shadow-md hover:border-primary/50',
                  'focus-visible:ring-2 focus-visible:ring-primary'
                )}
                onClick={() => setStep('fiat')}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && setStep('fiat')}
              >
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className={cn(
                      'p-3 rounded-xl',
                      'bg-blue-100 dark:bg-blue-900/30'
                    )}>
                      <SFIcon icon={sfIcons.bank} className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div className="flex-1">
                      <Heading level={4} className="mb-1">Bank Transfer</Heading>
                      <Text variant="sm" color="muted">
                        Receive USD, EUR, or MXN via wire
                      </Text>
                      <div className="flex items-center gap-2 mt-3">
                        <Text variant="caption" color="muted">
                          1-2 business days • Auto-converts to USDC
                        </Text>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* View Your Bank Accounts - Quick access to existing VAs */}
              {virtualAccountsData?.accounts && virtualAccountsData.accounts.length > 0 && (
                <button
                  onClick={handleViewAccounts}
                  className={cn(
                    'w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl',
                    'text-primary hover:bg-primary/5',
                    semanticTokens.motion.standard,
                    'focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none',
                    'min-h-[44px]'
                  )}
                >
                  <SFIcon icon={sfIcons.externalLink} className="h-4 w-4" />
                  <Text variant="sm" className="text-primary font-medium">
                    View your bank accounts ({virtualAccountsData.accounts.length})
                  </Text>
                </button>
              )}
            </div>
          )}

          {/* Step: Crypto - Select Chain */}
          {step === 'crypto' && !selectedChain && (
            <div className="grid gap-3">
              {SUPPORTED_CHAINS.map((chain) => (
                <Card
                  key={chain.id}
                  className={cn(
                    'cursor-pointer transition-all',
                    semanticTokens.motion.standard,
                    'hover:shadow-md hover:border-primary/50',
                    chain.recommended && 'border-primary/30'
                  )}
                  onClick={() => handleSelectChain(chain.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && handleSelectChain(chain.id)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <NetworkIcon network={chain.id} className="h-8 w-8" />
                        <div>
                          <div className="flex items-center gap-2">
                            <Text variant="md" className="font-medium">{chain.name}</Text>
                            {chain.recommended && (
                              <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                                Recommended
                              </span>
                            )}
                          </div>
                          <Text variant="caption" color="muted">{chain.currency} • {chain.fee} fees</Text>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Step: Crypto - Show Address */}
          {step === 'crypto' && selectedChain && (
            <div className="space-y-6">
              {depositAddressMutation.isPending ? (
                <div className="space-y-4">
                  <Skeleton className="h-48 w-48 mx-auto rounded-2xl" />
                  <Skeleton className="h-12 w-full rounded-xl" />
                </div>
              ) : generatedAddress ? (
                <>
                  {/* QR Code */}
                  <div className="flex justify-center">
                    <AppleQR
                      value={generatedAddress.address}
                      size={180}
                      bgColor="transparent"
                      fgColor="currentColor"
                    />
                  </div>

                  {/* Address */}
                  <div className="space-y-2">
                    <Text variant="caption" color="muted" className="text-center block">
                      Send only USDC on {SUPPORTED_CHAINS.find(c => c.id === selectedChain)?.name}
                    </Text>
                    <div
                      className={cn(
                        'flex items-center gap-2 p-3 rounded-xl cursor-pointer',
                        semanticTokens.bg.muted,
                        'hover:bg-muted/80',
                        semanticTokens.motion.standard
                      )}
                      onClick={() => handleCopy(generatedAddress.address)}
                      role="button"
                      tabIndex={0}
                    >
                      <code className="flex-1 text-sm font-mono break-all">
                        {generatedAddress.address}
                      </code>
                      <Button variant="ghost" size="icon" className="shrink-0">
                        {copied ? <SFIcon icon={sfIcons.success} className="h-4 w-4 text-green-500" /> : <SFIcon icon={sfIcons.copy} className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>

                  {/* Info */}
                  <div className={cn('p-4 rounded-xl', 'bg-blue-50 dark:bg-blue-950/30')}>
                    <Text variant="sm" color="muted">
                      Funds will appear in your SupaWallet after network confirmation.
                    </Text>
                  </div>
                </>
              ) : null}
            </div>
          )}

          {/* Step: Fiat - Select Currency */}
          {step === 'fiat' && fiatSubStep === 'select-currency' && (
            <div className="grid gap-3">
              {SUPPORTED_FIAT.map((fiat) => (
                <Card
                  key={fiat.id}
                  className={cn(
                    'cursor-pointer transition-all',
                    semanticTokens.motion.standard,
                    'hover:shadow-md hover:border-primary/50'
                  )}
                  onClick={() => handleSelectFiatCurrency(fiat.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && handleSelectFiatCurrency(fiat.id)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <CurrencyIcon symbol={fiat.id} size="lg" />
                        <div>
                          <Text variant="md" className="font-medium">{fiat.name}</Text>
                          <Text variant="caption" color="muted">{fiat.type}</Text>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Step: Fiat - Show/Select Account */}
          {step === 'fiat' && fiatSubStep === 'show-account' && !selectedVA && (
            <div className="space-y-4">
              {isLoadingVA ? (
                <div className="space-y-3">
                  {[1, 2].map((i) => (
                    <Skeleton key={i} className="h-20 w-full rounded-xl" />
                  ))}
                </div>
              ) : filteredVAs.length > 0 ? (
                <>
                  <Text variant="sm" color="muted">Select an existing account or create a new one</Text>
                  <div className="grid gap-3">
                    {filteredVAs.map((va) => (
                      <Card
                        key={va.id}
                        className={cn(
                          'cursor-pointer transition-all',
                          semanticTokens.motion.standard,
                          'hover:shadow-md hover:border-primary/50'
                        )}
                        onClick={() => handleSelectVA(va)}
                        role="button"
                        tabIndex={0}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <CurrencyIcon symbol={va.currency || selectedFiat || 'USD'} size="md" />
                              <div>
                                <Text variant="md" className="font-medium">
                                  {va.bankName || `${selectedFiat} Account`}
                                </Text>
                                <Text variant="caption" color="muted">
                                  {va.accountNumber ? `****${va.accountNumber.slice(-4)}` : 'Active'}
                                </Text>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-center py-8">
                  <SFIcon icon={sfIcons.bank} className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <Heading level={4} className="mb-2">No {selectedFiat} account yet</Heading>
                  <Text variant="sm" color="muted" className="mb-6">
                    Create a deposit account to receive {selectedFiat} transfers
                  </Text>
                </div>
              )}

              {/* Create new account button */}
              <Button
                variant={filteredVAs.length > 0 ? 'outline' : 'default'}
                className={cn('w-full min-h-[52px] rounded-xl')}
                onClick={handleCreateVA}
                disabled={virtualAccountMutation.isPending}
              >
                {virtualAccountMutation.isPending ? (
                  <>
                    <InlineLoadingIndicator size="md" className="mr-2" />
                    Creating...
                  </>
                ) : (
                  <>
                    <SFIcon icon={sfIcons.add} className="mr-2 h-5 w-5" />
                    Create {selectedFiat} Account
                  </>
                )}
              </Button>
            </div>
          )}

          {/* Step: Fiat - Show Bank Details */}
          {step === 'fiat' && fiatSubStep === 'show-account' && selectedVA && (
            <div className="space-y-6">
              {isLoadingFullDetails ? (
                <div className="space-y-3">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Skeleton key={i} className="h-16 w-full rounded-xl" />
                  ))}
                </div>
              ) : fullDetails ? (
                <>
                  {/* Beneficiary Information - CRITICAL for all transfers */}
                  {(fullDetails.beneficiaryName || fullDetails.beneficiaryAddress) && (
                    <div className="space-y-3">
                      <Text variant="sm" className="font-semibold text-foreground">Beneficiary Details</Text>
                      {fullDetails.beneficiaryName && (
                        <DetailRow label="Beneficiary Name" value={fullDetails.beneficiaryName} onCopy={handleCopy} important />
                      )}
                      {fullDetails.beneficiaryAddress && (
                        <DetailRow label="Beneficiary Address" value={fullDetails.beneficiaryAddress} onCopy={handleCopy} />
                      )}
                    </div>
                  )}

                  {/* Bank Information */}
                  <div className="space-y-3">
                    <Text variant="sm" className="font-semibold text-foreground">Bank Details</Text>
                    {fullDetails.bankName && (
                      <DetailRow label="Bank Name" value={fullDetails.bankName} onCopy={handleCopy} />
                    )}
                    {fullDetails.bankAddress && (
                      <DetailRow label="Bank Address" value={fullDetails.bankAddress} onCopy={handleCopy} />
                    )}

                    {/* SEPA/EUR - IBAN + BIC */}
                    {fullDetails.iban && (
                      <DetailRow label="IBAN" value={fullDetails.iban} onCopy={handleCopy} />
                    )}
                    {fullDetails.bic && (
                      <DetailRow label="BIC/SWIFT" value={fullDetails.bic} onCopy={handleCopy} />
                    )}

                    {/* ACH/USD - Account + Routing */}
                    {fullDetails.accountNumber && (
                      <DetailRow label="Account Number" value={fullDetails.accountNumber} onCopy={handleCopy} />
                    )}
                    {fullDetails.routingNumber && (
                      <DetailRow label="ACH Routing Number" value={fullDetails.routingNumber} onCopy={handleCopy} />
                    )}
                    {fullDetails.wireRoutingNumber && fullDetails.wireRoutingNumber !== fullDetails.routingNumber && (
                      <DetailRow label="Wire Routing Number" value={fullDetails.wireRoutingNumber} onCopy={handleCopy} />
                    )}
                    {fullDetails.accountType && (
                      <DetailRow label="Account Type" value={fullDetails.accountType} onCopy={handleCopy} />
                    )}

                    {/* SPEI/MXN - CLABE */}
                    {fullDetails.clabe && (
                      <DetailRow label="CLABE" value={fullDetails.clabe} onCopy={handleCopy} />
                    )}

                    {/* PIX/BRL */}
                    {fullDetails.pixKey && (
                      <DetailRow
                        label={fullDetails.pixKeyType ? `PIX (${fullDetails.pixKeyType})` : 'PIX Key'}
                        value={fullDetails.pixKey}
                        onCopy={handleCopy}
                      />
                    )}
                  </div>

                  {/* Reference - CRITICAL */}
                  {(fullDetails.reference || fullDetails.memo) && (
                    <div className="space-y-3">
                      <Text variant="sm" className="font-semibold text-foreground">Transfer Reference</Text>
                      {fullDetails.reference && (
                        <DetailRow label="Reference Number" value={fullDetails.reference} onCopy={handleCopy} important />
                      )}
                      {fullDetails.memo && (
                        <DetailRow label="Memo/Note" value={fullDetails.memo} onCopy={handleCopy} />
                      )}
                    </div>
                  )}

                  {/* Info */}
                  <div className={cn('p-4 rounded-xl', 'bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800')}>
                    <Text variant="sm" className="text-amber-800 dark:text-amber-200">
                      <strong>Important:</strong> Include the reference number in your bank transfer. Funds will auto-convert to USDC and appear in your SupaWallet within 1-2 business days.
                    </Text>
                  </div>
                </>
              ) : (
                /* Fallback to selectedVA data (used immediately after creation) */
                <>
                  {/* Beneficiary Information */}
                  {selectedVA.beneficiaryName && (
                    <div className="space-y-3">
                      <Text variant="sm" className="font-semibold text-foreground">Beneficiary Details</Text>
                      <DetailRow label="Beneficiary Name" value={selectedVA.beneficiaryName} onCopy={handleCopy} important />
                      {selectedVA.beneficiaryAddress && selectedVA.beneficiaryAddress !== selectedVA.bankAddress && (
                        <DetailRow label="Beneficiary Address" value={selectedVA.beneficiaryAddress} onCopy={handleCopy} />
                      )}
                    </div>
                  )}

                  {/* Bank Details */}
                  <div className="space-y-3">
                    <Text variant="sm" className="font-semibold text-foreground">Bank Details</Text>
                    {selectedVA.bankName && (
                      <DetailRow label="Bank Name" value={selectedVA.bankName} onCopy={handleCopy} />
                    )}
                    {selectedVA.bankAddress && (
                      <DetailRow label="Bank Address" value={selectedVA.bankAddress} onCopy={handleCopy} />
                    )}
                    {selectedVA.iban && (
                      <DetailRow label="IBAN" value={selectedVA.iban} onCopy={handleCopy} />
                    )}
                    {selectedVA.bic && (
                      <DetailRow label="BIC/SWIFT" value={selectedVA.bic} onCopy={handleCopy} />
                    )}
                    {selectedVA.accountNumber && (
                      <DetailRow label="Account Number" value={selectedVA.accountNumber} onCopy={handleCopy} />
                    )}
                    {selectedVA.routingNumber && (
                      <DetailRow label="Routing Number" value={selectedVA.routingNumber} onCopy={handleCopy} />
                    )}
                    {selectedVA.clabe && (
                      <DetailRow label="CLABE" value={selectedVA.clabe} onCopy={handleCopy} />
                    )}
                    {selectedVA.pixKey && (
                      <DetailRow label="PIX Key" value={selectedVA.pixKey} onCopy={handleCopy} />
                    )}
                  </div>

                  {/* Reference */}
                  {selectedVA.reference && (
                    <div className="space-y-3">
                      <Text variant="sm" className="font-semibold text-foreground">Transfer Reference</Text>
                      <DetailRow label="Reference Number" value={selectedVA.reference} onCopy={handleCopy} important />
                    </div>
                  )}

                  {/* Info */}
                  <div className={cn('p-4 rounded-xl', 'bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800')}>
                    <Text variant="sm" className="text-amber-800 dark:text-amber-200">
                      <strong>Important:</strong> Include the reference number in your bank transfer.
                    </Text>
                  </div>
                </>
              )}
            </div>
          )}

        </div>

        {/* Footer */}
        <SheetFooter className={cn(
          'flex-shrink-0 border-t pt-4',
          isMobile && 'pb-6'
        )}>
          {/* Show Copy All / Share buttons when viewing Bank Details */}
          {step === 'fiat' && fiatSubStep === 'show-account' && selectedVA ? (
            <div className="w-full space-y-3">
              {/* Primary actions: Copy All + Share */}
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className={cn('flex-1 min-h-[52px] rounded-xl gap-2')}
                  onClick={handleCopyAll}
                >
                  <SFIcon icon={sfIcons.copy} className="h-4 w-4" />
                  Copy All
                </Button>
                <Button
                  variant="default"
                  className={cn('flex-1 min-h-[52px] rounded-xl gap-2')}
                  onClick={handleShare}
                >
                  <Share2 className="h-4 w-4" />
                  Share
                </Button>
              </div>
              {/* Done button */}
              <Button
                variant="ghost"
                className={cn('w-full min-h-[44px] rounded-xl text-muted-foreground')}
                onClick={onClose}
              >
                Done
              </Button>
            </div>
          ) : (
            <Button
              variant="outline"
              className={cn('w-full min-h-[52px] rounded-xl')}
              onClick={onClose}
            >
              Done
            </Button>
          )}
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

// Helper component for bank detail rows
function DetailRow({
  label,
  value,
  onCopy,
  important = false
}: {
  label: string;
  value: string;
  onCopy: (text: string) => void;
  important?: boolean;
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    onCopy(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div
      className={cn(
        'flex items-center justify-between p-3 rounded-xl cursor-pointer',
        important ? 'bg-primary/5 border border-primary/20' : semanticTokens.bg.muted,
        'hover:bg-muted/80',
        semanticTokens.motion.standard
      )}
      onClick={handleCopy}
      role="button"
      tabIndex={0}
    >
      <div className="flex-1 min-w-0">
        <Text variant="caption" color="muted">{label}</Text>
        <Text variant="md" className={cn('font-mono break-all', important && 'text-primary font-semibold')}>
          {value}
        </Text>
      </div>
      <Button variant="ghost" size="icon" className="shrink-0 ml-2">
        {copied ? <SFIcon icon={sfIcons.success} className="h-4 w-4 text-green-500" /> : <SFIcon icon={sfIcons.copy} className="h-4 w-4" />}
      </Button>
    </div>
  );
}

